<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    modal
    :style="{ width: '80rem', height: '60rem' }"
    :breakpoints="{ '1199px': '85vw', '575px': '100vw' }"
  >
    <template #header>
      <div class="flex justify-between items-center w-full">
        <span class="text-lg font-semibold">
          {{ (selectedData && selectedData.flraNum) || "Preview" }}
          <span v-if="fileName">({{ fileName }})</span>
          <span
            v-if="
              fileExtension &&
              fileExtension !== Constants.enums.FILE_EXTENSION_TYPE.PDF
            "
          >
            ({{ fileExtension.toUpperCase() }} File)
          </span>
        </span>
        <div
          class="flex items-center gap-1"
          v-if="!hideActions"
        >
          <Button
            v-if="selectedData?.status === 3 && isReviewTab"
            raised
            severity="secondary"
            icon="pi pi-check-square"
            @click="$emit('approve')"
            label="Approve"
          />
          <Button
            text
            v-if="
              isInvoiceForm &&
              selectedData?.submission_status ===
                Constants.enums.INVOICE_STATUS.POSTED
            "
            icon="pi pi-envelope"
            severity="secondary"
            @click="$emit('sendEmail')"
          />
          <Button
            text
            v-if="
              // TODO: we should make this dynamic and configurable from DB.
              (isInvoiceForm &&
                selectedData?.submission_status ===
                  Constants.enums.INVOICE_STATUS.POSTED) ||
              true
            "
            icon="pi pi-download"
            variant="outlined"
            severity="secondary"
            @click="$emit('download')"
          />
        </div>
      </div>
    </template>

    <div
      style="
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
      "
    >
      <VuePdfEmbed
        v-if="
          pdfSrc && fileExtension === Constants.enums.FILE_EXTENSION_TYPE.PDF
        "
        annotation-layer
        text-layer
        :source="pdfSrc"
        style="width: 100%; height: 100%"
      />

      <!-- Image Preview -->
      <img
        v-else-if="isImageFile"
        :src="pdfSrc"
        alt="Image Preview"
        style="max-width: 100%; max-height: 100%; object-fit: contain"
      />

      <span v-else-if="!pdfSrc">Loading PDF...</span>

      <!-- Non-previewable file message -->
      <div
        v-else
        class="flex flex-col items-center justify-center p-12 text-center bg-gray-100 rounded-md"
        style="width: 80%; height: 80%"
      >
        <i class="pi pi-file text-8xl text-gray-400 mb-4"></i>
        <h3 class="text-xl font-bold text-gray-700 mb-2">
          File Preview Not Available
        </h3>
        <p class="text-gray-600 mb-2">
          {{ fileExtension.toUpperCase() }} file format cannot be previewed in
          the browser. Please download the file to view its contents.
        </p>
      </div>
    </div>
  </Dialog>
</template>

<script>
  import { defineComponent, computed } from "vue";
  import VuePdfEmbed from "vue-pdf-embed";
  import Constants from "@/utils/constants";

  export default defineComponent({
    name: "PdfPreviewDialog",
    components: {
      VuePdfEmbed
    },
    props: {
      // Controls dialog visibility
      visible: {
        type: Boolean,
        required: true
      },
      selectedData: {
        // Data for the selected FLRA
        type: Object,
        default: () => ({})
      },
      // Source URL or data for the PDF
      pdfSrc: String,
      fileName: {
        type: String,
        default: null
      },
      activeTab: String,
      hideActions: {
        type: Boolean,
        default: false
      }
    },
    setup(props) {
      // Extract file extension (Default: PDF)
      const fileExtension = computed(() => {
        if (props.selectedData?.fileExtension) {
          return props.selectedData.fileExtension;
        }

        if (props.pdfSrc && props.pdfSrc.includes(".")) {
          return props.pdfSrc.split(".").pop().toLowerCase();
        }

        return "pdf";
      });

      // Check if it's image file format
      const isImageFile = computed(() => {
        const imgExtensions = [
          Constants.enums.IMAGE_EXTENSION_TYPE.JPG,
          Constants.enums.IMAGE_EXTENSION_TYPE.JPEG,
          Constants.enums.IMAGE_EXTENSION_TYPE.PNG,
          Constants.enums.IMAGE_EXTENSION_TYPE.GIF,
          Constants.enums.IMAGE_EXTENSION_TYPE.WEBP,
          Constants.enums.IMAGE_EXTENSION_TYPE.SVG
        ];
        return imgExtensions.includes(fileExtension.value);
      });

      return { fileExtension, isImageFile, Constants };
    },
    computed: {
      // Check if the active tab is FLRA Review
      isReviewTab() {
        return (
          this.activeTab === Constants.enums.FLRA_FORMS_CATEGORY.FLRA_REVIEW
        );
      },
      isInvoiceForm() {
        return (
          this.selectedData?.form_template_id ===
          Constants.enums.STATIC_FORM_TEMPLATE_ID?.INVOICE_FORMS
        );
      }
    },
    emits: ["update:visible", "download", "approve", "sendEmail"]
  });
</script>
